'use client';

import { useContext } from 'react';
import { SnackbarContext } from './SnackbarProvider';
import type { SnackbarProps } from './types';

export function useSnackbar() {
  const context = useContext(SnackbarContext);

  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }

  const { show, close, closeAll, snackbars } = context;

  const showSuccess = (
    message: string,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'variant'>>,
  ) => {
    return show({ ...options, message, variant: 'success' });
  };

  const showError = (
    message: string,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'variant'>>,
  ) => {
    return show({ ...options, message, variant: 'error' });
  };

  const showWarning = (
    message: string,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'variant'>>,
  ) => {
    return show({ ...options, message, variant: 'warning' });
  };

  const showDefault = (
    message: string,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'variant'>>,
  ) => {
    return show({ ...options, message, variant: 'default' });
  };

  const showProduct = (
    title: string,
    message: string,
    image: string,
    options?: Partial<Omit<SnackbarProps, 'id' | 'title' | 'message' | 'image'>>,
  ) => {
    return show({ ...options, title, message, image });
  };

  const showWithNavigation = (
    message: string,
    onNavigate: () => void,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'action'>>,
  ) => {
    return show({
      ...options,
      message,
      action: { onAction: onNavigate },
    });
  };

  const showWithTextAction = (
    message: string,
    actionText: string,
    onAction: () => void,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'action'>>,
  ) => {
    return show({
      ...options,
      message,
      action: { text: actionText, onAction },
    });
  };

  const showWithIconAction = (
    message: string,
    icon: React.ReactNode,
    onAction: () => void,
    options?: Partial<Omit<SnackbarProps, 'id' | 'message' | 'action'>>,
  ) => {
    return show({
      ...options,
      message,
      action: { icon, onAction },
    });
  };

  return {
    show,
    close,
    closeAll,
    snackbars,

    success: showSuccess,
    error: showError,
    warning: showWarning,
    default: showDefault,
    product: showProduct,

    withNavigation: showWithNavigation,
    withTextAction: showWithTextAction,
    withIconAction: showWithIconAction,
  };
}
